import { useCallback, useEffect } from "react";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { UserLoginSignUpScreen } from "./user-login-sign-up-screen.tsx";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/base/dialog.tsx";
import { useDialogContext } from "../utils/dialog-context.tsx";
import { useLoginDialogContext } from "./utils/login-dialog-context.tsx";

export interface UserLoginSignUpDialogProps {
  loginTriggerElementSelector: string;
  signUpTriggerElementSelector: string;
}

export const UserLoginSignUpDialog: React.FC<UserLoginSignUpDialogProps> = (props) => {
  const dialogContext = useDialogContext();
  if (!dialogContext) {
    throw new Error("DialogContext is not provided");
  }
  const loginDialogContext = useLoginDialogContext();
  if (!loginDialogContext) {
    throw new Error("LoginDialogContext is not provided");
  }

  // we don't provide any callbacks when clicking on login or sign-up buttons
  // default behaviour is just reloading the page
  const openLoginPopup = useCallback((event: Event) => {
    event.preventDefault();
    loginDialogContext.update({
      initialTab: "sign-in",
      loginCallback: undefined,
      loginSuccessMessage: undefined,
    });
    dialogContext.setOpen(true);
  }, []);
  const openSignUpPopup = useCallback((event: Event) => {
    event.preventDefault();
    loginDialogContext.update({
      initialTab: "create-account",
      loginCallback: undefined,
      loginSuccessMessage: undefined,
    });
    dialogContext.setOpen(true);
  }, []);

  // bind to buttons
  useEffect(() => {
    const loginButtons = document.querySelectorAll(props.loginTriggerElementSelector);
    loginButtons.forEach((element) => {
      element.addEventListener("click", openLoginPopup);
    });

    const signUpButtons = document.querySelectorAll(props.signUpTriggerElementSelector);
    signUpButtons.forEach((element) => {
      element.addEventListener("click", openSignUpPopup);
    });

    return () => {
      loginButtons.forEach((element) => {
        element.removeEventListener("click", openLoginPopup);
      });

      signUpButtons.forEach((element) => {
        element.removeEventListener("click", openSignUpPopup);
      });
    };
  }, []);

  useEffect(() => {
    globalThis.openLoginPopup = (callback, successMessage) => {
      loginDialogContext.update({
        initialTab: "sign-in",
        loginCallback: callback,
        loginSuccessMessage: successMessage,
      });
      dialogContext.setOpen(true);
    };
    return () => {
      delete globalThis.openLoginPopup;
    };
  }, []);

  return (
    <Dialog open={dialogContext.open} onOpenChange={dialogContext.setOpen} modal={true}>
      <DialogContent className="twp p-0 max-w-fit w-fit border-none m-2.5 md:m-5">
        <VisuallyHidden.Root>
          {/* Used for accessibility only */}
          <DialogTitle>Log in or sign up for an account</DialogTitle>
          <DialogDescription>
            Save your search preferences and favourite properties. We’ll keep you up to date with the latest property
            alerts.
          </DialogDescription>
        </VisuallyHidden.Root>
        <UserLoginSignUpScreen initialTab={loginDialogContext.state.initialTab} />
      </DialogContent>
    </Dialog>
  );
};
